import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fetchCustomerDiamondPointsRequest } from "../../../redux/Order/RedeemOrdersSlice";

const formatDateTime = (value) => {
  if (!value) return "N.A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N.A";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const customerName = (customer) => {
  if (!customer) return "N.A";
  const name = [customer.firstName, customer.middleName, customer.lastName]
    .filter(Boolean)
    .join(" ");
  return name || "N.A";
};

const typeColor = (type) => {
  switch (type) {
    case "Credit":
      return "green";
    case "Redeem":
      return "blue";
    case "Expired":
      return "red";
    default:
      return "inherit";
  }
};

const headerCellSx = { backgroundColor: "#b293c7ff", fontWeight: 700 };

const DiamondPointsOrdersTable = () => {
  const dispatch = useDispatch();
  const redeemOrders = useSelector((state) => state.redeemOrders || {});
  const {
    customerDiamondPointsData = [],
    totalCustomerDiamondPoints = 0,
    loading = false,
  } = redeemOrders;

  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    dispatch(
      fetchCustomerDiamondPointsRequest({
        start: page * limit,
        limit,
        search,
      }),
    );
  }, [dispatch, page, limit, search]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        break;
      case "changeRowsPerPage":
        setLimit(tableState.rowsPerPage);
        setPage(0);
        break;
      case "search":
        setSearch(tableState.searchText || "");
        setPage(0);
        break;
      default:
        break;
    }
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setOpenDetails(true);
  };

  const handleClose = () => {
    setOpenDetails(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Latest Activity",
      minWidth: 180,
      render: (val, row) => formatDateTime(row?.createdAt),
    },
    {
      field: "customer",
      headerName: "Customer Name",
      minWidth: 180,
      render: (val, row) => customerName(row.customer),
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 140,
      render: (val, row) => row.customer?.phone || "N.A",
    },
    {
      field: "totalPoints",
      headerName: "Total Points",
      minWidth: 130,
      render: (val, row) => row.totalPoints ?? 0,
    },
    {
      field: "totalUsedPoints",
      headerName: "Used Points",
      minWidth: 130,
      render: (val, row) => row.totalUsedPoints ?? 0,
    },
    {
      field: "totalAvailablePoints",
      headerName: "Available Points",
      minWidth: 160,
      render: (val, row) => row.totalAvailablePoints ?? 0,
    },
    {
      field: "details",
      headerName: "Details",
      minWidth: 90,
      render: (val, row) => (
        <button
          style={{
            backgroundColor: "#ffc107",
            color: "#000",
            border: "none",
            padding: "5px 10px",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={() => handleView(row)}
        >
          View
        </button>
      ),
    },
  ];

  const schemes = selectedRow?.schemes || [];

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Customer Diamond Points
        </Typography>

        <CardContent sx={{ height: "580px", width: "100%" }}>
          <TableCompo
            columns={columns}
            data={[...JSON.parse(JSON.stringify(customerDiamondPointsData))]}
            totalCount={totalCustomerDiamondPoints}
            page={page}
            limit={limit}
            loading={loading}
            onTableChange={handleTableChange}
          />
        </CardContent>
      </Box>

      <Dialog open={openDetails} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Diamond Point Details
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRow ? (
            <>
              <Typography variant="h6">Customer Info</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <b>Name</b>
                    </TableCell>
                    <TableCell>{customerName(selectedRow.customer)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Phone</b>
                    </TableCell>
                    <TableCell>{selectedRow.customer?.phone || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Email</b>
                    </TableCell>
                    <TableCell>{selectedRow.customer?.email || "N.A"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Typography variant="h6">Schemes</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Scheme</TableCell>
                    <TableCell sx={headerCellSx}>Points</TableCell>
                    <TableCell sx={headerCellSx}>Used Points</TableCell>
                    <TableCell sx={headerCellSx}>Available Points</TableCell>
                    <TableCell sx={headerCellSx}>Expire At</TableCell>
                    <TableCell sx={headerCellSx}>Latest Activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schemes.length > 0 ? (
                    schemes.map((scheme, index) => {
                      const types = [
                        ...new Set(
                          (scheme.records || [])
                            .map((record) => record.type)
                            .filter(Boolean),
                        ),
                      ];
                      const expireDates = [
                        ...new Set(
                          (scheme.records || [])
                            .map((record) => record.expireAt)
                            .filter(Boolean),
                        ),
                      ];

                      return (
                        <TableRow key={`${scheme.source || "scheme"}-${index}`}>
                          <TableCell>{scheme.source || "N.A"}</TableCell>
                          <TableCell>{scheme.points ?? 0}</TableCell>
                          <TableCell>{scheme.used_points ?? 0}</TableCell>
                          <TableCell>{scheme.availablePoints ?? 0}</TableCell>
                          <TableCell>
                            {expireDates.length > 0
                              ? expireDates.map((date) => (
                                  <Box
                                    key={date}
                                    component="span"
                                    sx={{ display: "block" }}
                                  >
                                    {formatDateTime(date)}
                                  </Box>
                                ))
                              : "N.A"}
                          </TableCell>
                          <TableCell>{formatDateTime(scheme.createdAt)}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7}>N.A</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography>Data Not Available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DiamondPointsOrdersTable;
